import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import Button, { ButtonColor, ButtonSize, ButtonVariant } from 'src/components/atoms/Button';
import Gap from 'src/components/atoms/Gap';
import { Icon } from 'src/components/atoms/Icon';
import { Row } from 'src/components/atoms/Row';
import Text, { TextVariant } from 'src/components/atoms/Text';
import {
  CALCULATION_NOTE_MIN_LENGTH,
  CalculationNoteField,
  PreviousNoteSummary
} from 'src/components/molecules/CalculationNote';
import { ButtonRow } from 'src/components/molecules/Carousel/styles';
import { useEntryById } from 'src/features/entries/hooks';
import useUserRoles from 'src/features/auth/hooks/useUserRoles';
import {
  actions,
  calculateIncomeThunk,
  eligibleDocuments,
  MISMATCH_CODES,
  selectors as incomeVerificationSelectors
} from 'src/features/incomeVerification';
import api from 'src/features/incomeVerification/api';
import type { CalculationDocument } from 'src/features/incomeVerification/constants';
import {
  useCalculationDocuments,
  useCalculationOverride
} from 'src/features/incomeVerification/hooks';
import {
  applyDocumentEdit,
  buildIncomeCalcMetadata,
  enrichDocumentWithCalculation,
  formatApiDate,
  getIncomeDocumentsFromSession,
  getIsIVDocumentEmpty,
  parseIncomeCalculationDocuments
} from 'src/features/incomeVerification/utils';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import useSnackbarProvider from 'src/hooks/useSnackbarProvider';
import IncomeCalculationTileForm from 'src/pages/IncomeCalculationPage/components/IncomeCalculationTileForm/IncomeCalculationTileForm';
import { FeatureFlagKey } from 'src/ts/enums';
import { IncomeVerificationEnum } from 'src/ts/enums/session';
import { IncomeVerification } from 'src/ts/interfaces/incomeVerification';
import CalculationHeader from './components/CalculationHeader';
import SendReportModal from './components/SendReportModal';
import {
  Content,
  ErrorWarningMessage,
  IncomeResult,
  IncomeVerificationTiles,
  MismatchWarningMessage,
  NotesCard,
  TileSectionHeader
} from './styles';

const noopSave = (_data: IncomeVerification) => undefined;

function mapCalcDocToIV(doc: CalculationDocument, index: number): IncomeVerification {
  return {
    index,
    id: doc.document_id,
    docId: doc.document_id ?? '',
    file: '',
    fileName: '',
    payPeriodStartDate: doc.document_start_date ? formatApiDate(doc.document_start_date) : '',
    payPeriodEndDate: doc.document_end_date ? formatApiDate(doc.document_end_date) : '',
    payPeriodDateRange:
      doc.document_start_date && doc.document_end_date
        ? `${formatApiDate(doc.document_start_date)} - ${formatApiDate(doc.document_end_date)}`
        : '',
    recipientName: doc.applicant_name || '',
    grossPay: doc.gross_income?.toString() || '',
    incomeSource: doc.income_source || '',
    type: doc.document_type || 'PAYSTUB',
    hasNonRecurringIncome: doc.has_non_recurring_income ?? false,
    basePayIncomeAmount:
      doc.base_pay_income_amount != null ? String(doc.base_pay_income_amount) : ''
  };
}

const IncomeCalculationPage = () => {
  const { entryId, incomeVerificationReviewId } = useParams();
  const isCalcDispute = !incomeVerificationReviewId;
  const sessionKey = incomeVerificationReviewId ?? `calc-dispute-${entryId}`;
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAccountRepresentative } = useUserRoles();
  const calculateIncomeIsLoading = useSelector(incomeVerificationSelectors.isLoading);
  const calculateIncomeResult = useSelector(incomeVerificationSelectors.selectCalculateIncomeData);
  const calculateIncomeIsError = useSelector(incomeVerificationSelectors.isError);
  const calculateIncomeError = useSelector(incomeVerificationSelectors.selectCalculateIncomeError);
  const [showSendReportModal, setShowSendReportModal] = useState(false);
  const redirectPath = searchParams.get('redirectPath');

  const showOverrides = isCalcDispute;
  const isNoteEnabled = useFeatureFlag(FeatureFlagKey.ITL_3287_SPECIALTY_CALCULATION_NOTE);
  const { entry } = useEntryById(entryId ?? '');
  const applicantName = [entry?.applicant?.first_name, entry?.applicant?.last_name]
    .filter(Boolean)
    .join(' ');
  const applicantId = entry?.applicant?.id;
  const companyId = entry?.folder?.company_id;
  const propertyId = entry?.folder?.property_id;

  const { SnackTypes, showSnackbar, VariantType } = useSnackbarProvider();
  const {
    calculationId: initialCalculationId,
    previousNote,
    noteRequired,
    isLoadingDocs,
    calculationDocuments,
    documentErrorsMap,
    mismatchNamesMap,
    overrideSelections,
    setOverrideSelections
  } = useCalculationDocuments({ entryId, enabled: showOverrides, applicantName });
  const [lastCalculationId, setLastCalculationId] = useState<string | undefined>();
  useEffect(() => {
    if (initialCalculationId && !lastCalculationId) {
      setLastCalculationId(initialCalculationId);
    }
  }, [initialCalculationId, lastCalculationId]);
  const [overrideIncomeDisplay, setOverrideIncomeDisplay] = useState<string | undefined>();
  const [isRecalculating, setIsRecalculating] = useState(false);
  const { onCalculationOverride } = useCalculationOverride();
  const noteMethods = useForm<{ calculation_note: string }>({
    mode: 'onChange',
    defaultValues: { calculation_note: '' }
  });
  const calculationNote = noteMethods.watch('calculation_note').trim();
  const showNoteField = isCalcDispute && noteRequired && !!lastCalculationId;
  const showPreviousNote = isNoteEnabled && isCalcDispute && previousNote !== undefined;

  const sessionDocuments: IncomeVerification[] = getIncomeDocumentsFromSession(sessionKey);

  // In calc dispute mode, session storage is empty (user arrives from folder page,
  // not from the IV review flow). Fall back to calculationDocuments from the API.
  const documents: IncomeVerification[] = useMemo(() => {
    if (sessionDocuments.length > 0) return sessionDocuments;
    if (!isCalcDispute || !calculationDocuments.length) return [];

    return calculationDocuments
      .filter((doc) => doc.source_type !== 'CONNECTED_PAYROLL')
      .map((doc, index) => mapCalcDocToIV(doc, index));
  }, [sessionDocuments, isCalcDispute, calculationDocuments]);

  const calculateIncome = useDebouncedCallback(() => {
    const documents = getIncomeDocumentsFromSession(sessionKey);
    if (sessionKey && documents && !calculateIncomeIsLoading) {
      const ivDocuments = documents.filter((document) => {
        return eligibleDocuments.includes(document.type) && !getIsIVDocumentEmpty(document);
      });

      if (ivDocuments.length) {
        const incomeDocuments = parseIncomeCalculationDocuments(ivDocuments);

        dispatch(
          calculateIncomeThunk({
            submissionId: incomeVerificationReviewId as string,
            documents: incomeDocuments
          })
        );
      } else {
        dispatch(actions.resetIncomeVerification());
      }
    }
  }, 500);

  useEffect(() => {
    if (!isCalcDispute) {
      calculateIncome();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Recalculate with override selections
  const recalculateWithSelections = useCallback(
    async (selections: Record<string, boolean>) => {
      if (!entryId) return;
      const sessionDocs = getIncomeDocumentsFromSession(sessionKey);
      const ivDocuments = sessionDocs.filter(
        (doc) => eligibleDocuments.includes(doc.type) && !getIsIVDocumentEmpty(doc)
      );
      const parsed = parseIncomeCalculationDocuments(ivDocuments);

      // When session docs are empty (initial calc dispute state), fall back
      // to calculationDocuments which already have API-populated fields.
      const uploadDocs =
        parsed.length > 0
          ? parsed
          : calculationDocuments
              .filter((doc) => doc.source_type !== 'CONNECTED_PAYROLL')
              .map((doc) => ({
                document_id: doc.document_id,
                applicant_name: doc.applicant_name || '',
                document_start_date: doc.document_start_date || '',
                document_end_date: doc.document_end_date || '',
                gross_income: Number(doc.gross_income) || 0,
                income_source: doc.income_source || '',
                metadata: buildIncomeCalcMetadata(
                  doc.base_pay_income_amount,
                  doc.has_non_recurring_income
                )
              }));

      const documentsWithOverrides = uploadDocs.map((doc) => {
        const docErrors = documentErrorsMap[doc.document_id] ?? [];
        const mismatchCodes = docErrors.filter((c) => MISMATCH_CODES.includes(c));
        const hasMismatch = mismatchCodes.length > 0;
        const isIncluded = selections[doc.document_id];
        return {
          ...doc,
          ...(hasMismatch && isIncluded ? { ignore_errors: mismatchCodes } : {})
        };
      });

      // Also include connected payroll docs if they have overrides
      const payrollOverrides = calculationDocuments
        .filter((doc) => doc.source_type === 'CONNECTED_PAYROLL')
        .map((doc) => {
          const docErrors = documentErrorsMap[doc.document_id] ?? [];
          const mismatchCodes = docErrors.filter((c: number) => MISMATCH_CODES.includes(c));
          const hasMismatch = mismatchCodes.length > 0;
          const isIncluded = selections[doc.document_id];
          return {
            document_id: doc.document_id,
            applicant_name: doc.applicant_name || '',
            document_start_date: doc.document_start_date || '',
            document_end_date: doc.document_end_date || '',
            gross_income: Number(doc.gross_income) || 0,
            income_source: doc.income_source || '',
            document_source_type: 'CONNECTED_PAYROLL',
            metadata: buildIncomeCalcMetadata(0, false),
            ...(hasMismatch && isIncluded ? { ignore_errors: mismatchCodes } : {})
          };
        });

      const allDocuments = [...documentsWithOverrides, ...payrollOverrides];

      try {
        setIsRecalculating(true);
        const response = await api.calculateIncomeOverride(entryId, allDocuments);
        // defaultInstance interceptor returns response.data, so response IS the JSON body.
        // Success/warning responses wrap income in {results: {monthly_income, ...}}.
        // reference_id may be top-level or inside results depending on API version.
        const result = response?.data;
        const incomeResults = result?.results;
        setLastCalculationId(result?.reference_id ?? incomeResults?.reference_id);
        if (incomeResults?.monthly_income != null) {
          setOverrideIncomeDisplay(incomeResults.monthly_income.toFixed(2));
        }
      } catch {
        showSnackbar(VariantType.error, 'Failed to recalculate income', SnackTypes.none);
      } finally {
        setIsRecalculating(false);
      }
    },
    [
      entryId,
      sessionKey,
      documentErrorsMap,
      calculationDocuments,
      showSnackbar,
      VariantType,
      SnackTypes
    ]
  );

  // Initial recalculation for calc dispute mode — fires once when calculation docs load
  const hasInitiallyRecalculated = useRef(false);
  useEffect(() => {
    if (isCalcDispute && calculationDocuments.length > 0 && !hasInitiallyRecalculated.current) {
      hasInitiallyRecalculated.current = true;
      recalculateWithSelections(overrideSelections);
    }
  }, [isCalcDispute, calculationDocuments.length, recalculateWithSelections]);

  const debouncedRecalculate = useDebouncedCallback(
    (selections: Record<string, boolean>) => recalculateWithSelections(selections),
    300
  );

  const toggleOverrideSelection = useCallback(
    (docId: string) => {
      const next = { ...overrideSelections, [docId]: !overrideSelections[docId] };
      setOverrideSelections(next);
      debouncedRecalculate(next);
    },
    [overrideSelections, setOverrideSelections, debouncedRecalculate]
  );

  const handleOnSave = (data: IncomeVerification) => {
    applyDocumentEdit(documents, data);
    window.sessionStorage.setItem(
      `${IncomeVerificationEnum.INCOME_VERIFICATION_DOCUMENTS}${sessionKey}`,
      JSON.stringify(documents)
    );
    if (isCalcDispute) {
      debouncedRecalculate(overrideSelections);
    } else {
      calculateIncome();
    }
  };

  const onSendReport = () => {
    setShowSendReportModal(true);
  };

  const previousClicked = () => {
    window.sessionStorage.setItem(
      `${IncomeVerificationEnum.INCOME_VERIFICATION_DOCUMENTS_DISPLAY_LAST_PAGE}${sessionKey}`,
      'true'
    );
    navigate(-1);
  };

  const hasAnyOverrideSelected = Object.values(overrideSelections).some((v) => v);
  const mismatchDocCount = Object.keys(overrideSelections).length;
  const hasNameMismatchDocs = mismatchDocCount > 0;

  // Determine income display — use override income when available
  const incomeDisplay = overrideIncomeDisplay ?? calculateIncomeResult?.data;
  const isLoadingIncome = calculateIncomeIsLoading || isRecalculating;

  // Separate connected payroll docs
  const connectedPayrollDocs = useMemo(() => {
    if (!calculationDocuments || !showOverrides) return [];
    return calculationDocuments.filter((doc) => doc.source_type === 'CONNECTED_PAYROLL');
  }, [calculationDocuments, showOverrides]);

  return (
    <>
      <Content>
        <CalculationHeader />
        {hasNameMismatchDocs && showOverrides && (
          <MismatchWarningMessage data-testid="mismatch-warning-message">
            {mismatchDocCount} document{mismatchDocCount !== 1 ? 's were' : ' was'} excluded from
            the income calculation due to name mismatches. Check the boxes below to include them.
          </MismatchWarningMessage>
        )}
        <IncomeVerificationTiles>
          {showOverrides && documents.length > 0 && connectedPayrollDocs.length > 0 && (
            <TileSectionHeader>Document Upload</TileSectionHeader>
          )}
          <Row columns={3} wrap="wrap" justify={'space-between'}>
            {documents.map((document: IncomeVerification) => {
              if (document.index > -1) {
                const calcDoc = showOverrides
                  ? calculationDocuments.find((cd) => cd.document_id === document.id)
                  : undefined;
                const enrichedDoc = enrichDocumentWithCalculation(document, calcDoc);
                return (
                  <Row.Col size={1} key={document.docId}>
                    <IncomeCalculationTileForm
                      reason_codes={calculateIncomeResult?.reason_codes ?? []}
                      document={enrichedDoc}
                      onSave={handleOnSave}
                      entryId={entryId}
                      incomeVerificationReviewId={incomeVerificationReviewId}
                      applicantId={applicantId}
                      companyId={companyId}
                      propertyId={propertyId}
                      {...(showOverrides
                        ? {
                            documentErrors: documentErrorsMap[document.id],
                            isIncludedInCalculation: overrideSelections[document.id],
                            onToggleInclude: () => toggleOverrideSelection(document.id),
                            mismatchNames: mismatchNamesMap[document.id]
                          }
                        : {})}
                    />
                  </Row.Col>
                );
              }
              return null;
            })}
          </Row>
          {showOverrides && connectedPayrollDocs.length > 0 && (
            <>
              <TileSectionHeader>Connected Payroll</TileSectionHeader>
              <Row columns={3} wrap="wrap" justify={'space-between'}>
                {connectedPayrollDocs.map((doc: CalculationDocument) => {
                  const cpDoc: IncomeVerification = mapCalcDocToIV(doc, -1);
                  const docErrors = documentErrorsMap[doc.document_id] ?? [];
                  const hasMismatch = docErrors.some((c: number) => MISMATCH_CODES.includes(c));
                  return (
                    <Row.Col size={1} key={doc.document_id}>
                      <IncomeCalculationTileForm
                        reason_codes={calculateIncomeResult?.reason_codes ?? []}
                        document={cpDoc}
                        onSave={noopSave}
                        sourceType="CONNECTED_PAYROLL"
                        entryId={entryId}
                        incomeVerificationReviewId={incomeVerificationReviewId}
                        applicantId={applicantId}
                        companyId={companyId}
                        propertyId={propertyId}
                        documentErrors={docErrors.length > 0 ? docErrors : undefined}
                        isIncludedInCalculation={
                          hasMismatch ? overrideSelections[doc.document_id] : undefined
                        }
                        onToggleInclude={
                          hasMismatch ? () => toggleOverrideSelection(doc.document_id) : undefined
                        }
                        mismatchNames={mismatchNamesMap[doc.document_id]}
                      />
                    </Row.Col>
                  );
                })}
              </Row>
            </>
          )}
          {isNoteEnabled &&
            isCalcDispute &&
            initialCalculationId &&
            !isLoadingDocs &&
            calculationDocuments.length === 0 && (
              <Text variant={TextVariant.normal} testId="calculation-no-documents">
                This calculation has no documents.
              </Text>
            )}
          <IncomeResult>
            {!isCalcDispute && calculateIncomeResult?.status === 'error' ? (
              'Monthly Income Not Available'
            ) : (
              <>
                Monthly Income:&nbsp;{isLoadingIncome && <span>calculating...</span>}
                {!isLoadingIncome && incomeDisplay && (
                  <span data-testid="monthly-income-value">${incomeDisplay}</span>
                )}
              </>
            )}
          </IncomeResult>
          {!isLoadingIncome && calculateIncomeIsError && calculateIncomeError && (
            <ErrorWarningMessage status="error" data-testid="calculate-income-error">
              {calculateIncomeError}
            </ErrorWarningMessage>
          )}
        </IncomeVerificationTiles>
        {(showPreviousNote || showNoteField) && (
          <NotesCard data-testid="calculation-notes">
            {showPreviousNote && <PreviousNoteSummary previousNote={previousNote ?? null} />}
            {showNoteField && (
              <FormProvider {...noteMethods}>
                <CalculationNoteField />
              </FormProvider>
            )}
          </NotesCard>
        )}
        <Gap height={2} />
        <Row>
          <Row.Col size={1} alignContent="center">
            <Row justify="center">
              {!isAccountRepresentative && (
                <Button
                  name="button-send-report"
                  data-testid="send-report-button"
                  onClick={onSendReport}
                  size={ButtonSize.normal}
                  color={ButtonColor.primary}
                  variant={ButtonVariant.contained}
                  isDisabled={
                    isLoadingIncome ||
                    (showNoteField && calculationNote.length < CALCULATION_NOTE_MIN_LENGTH)
                  }
                >
                  Send Report
                </Button>
              )}
            </Row>
          </Row.Col>
        </Row>
        <Gap height={5} />
        <ButtonRow>
          <Row.Col size={3}>
            <Button
              name="button-back-to-income-verification-flow"
              variant={ButtonVariant.outline}
              className="income-verification-buttons"
              onClick={previousClicked}
            >
              <Icon icon="left-arrow" />
            </Button>
          </Row.Col>
          <Row.Col alignContent="center" size={6}>
            <Row justify="center" gap={0.15}>
              {documents.map((document) => {
                return (
                  <Row.Col key={document.docId}>
                    <Icon key={document.docId} icon="step-circle" />
                  </Row.Col>
                );
              })}
              <Row.Col>
                <Icon icon="current-step-circle" />
              </Row.Col>
            </Row>
            <Row justify="center">
              <Row.Col>
                <Text variant={TextVariant.h4}>
                  Step {documents.length + 1} of {documents.length + 1}
                </Text>
              </Row.Col>
            </Row>
          </Row.Col>
          <Row.Col size={3}>
            <div></div>
          </Row.Col>
        </ButtonRow>
      </Content>
      <SendReportModal
        selectedEntryId={entryId as string}
        showSendReportModal={showSendReportModal}
        setShowSendReportModal={setShowSendReportModal}
        hasOverrideSelections={hasAnyOverrideSelected}
        lastCalculationId={lastCalculationId}
        onCalculationOverride={onCalculationOverride}
        sessionKey={sessionKey}
        isCalcDispute={isCalcDispute}
        note={showNoteField ? calculationNote : undefined}
        onConfirm={async () => {
          // Clean up session storage for this entry
          window.sessionStorage.removeItem(
            `${IncomeVerificationEnum.INCOME_VERIFICATION_DOCUMENTS}${sessionKey}`
          );
          window.sessionStorage.removeItem(`income-verification-override-selections-${entryId}`);
          const route = redirectPath || '/my-work';
          navigate(route);
        }}
      />
    </>
  );
};

export default IncomeCalculationPage;
